import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Question } from '../../content';
import { playSoundIntent } from '../../utils/sound/soundIntent';
import type { Phase } from './useQuestionFlow';

/**
 * useSessionTimer — all time-based concerns for a practice session.
 *
 * Extracted from PracticeSession.tsx as part of the F-FE-1 god-component
 * split (K12: custom hooks, not mini-components). Owns three timers:
 *
 *  1. Fluency elapsed timer — `elapsedMs`, ticking at 4 Hz (250 ms) during
 *     the question phase of a Fluency session. The 250 ms interval is the
 *     F-PE-1 mitigation (was 10 Hz → ~600 re-renders per sprint). PRESERVED.
 *  2. Skip-eligibility timer — `canSkipTimePassed` flips true after 20 s on
 *     the current question, one input to the "Ik weet het echt niet" gate.
 *  3. Idle-nudge cluster (§16.H v2) — a per-question, cooldown-gated Djay
 *     bubble that appears after a length-scaled timeout of inactivity.
 *
 * The hook owns its own timer refs and clears them on unmount, so the
 * PracticeSession orchestrator does not need to know about them.
 *
 * HOOKS-ORDER SAFETY: called unconditionally at the top of PracticeSession,
 * above every early return.
 */
export const SKIP_TIME_THRESHOLD_MS = 20000;

// ── Idle nudge tuning (§16.H v2) ─────────────────────────────
const IDLE_BASE_MS = 45000;
const IDLE_PER_20_CHARS_MS = 1000;
const IDLE_MAX_MS = 90000;
const IDLE_GLOBAL_COOLDOWN_MS = 60000;
interface UseSessionTimerParams {
  isFluency: boolean;
  phase: Phase;
  currentQuestionIndex: number;
  isCheatSheetOpen: boolean;
  sessionQuestions: Question[];
  /** Ref to the current question's start timestamp (owned by useQuestionFlow). */
  questionStartTimeRef: React.MutableRefObject<number>;
}
export interface SessionTimer {
  /** Fluency elapsed milliseconds (0 outside Fluency question phase). */
  elapsedMs: number;
  /** True once the learner has spent ≥20 s on the current question. */
  canSkipTimePassed: boolean;
  /** True while the idle-nudge Djay bubble should be visible. */
  isIdle: boolean;
  /** Force-hide the idle bubble (e.g. on dismiss / interaction). */
  setIsIdle: React.Dispatch<React.SetStateAction<boolean>>;
  /** Dismiss the idle bubble. */
  dismissIdle: () => void;
  /** Restart the idle countdown (called on any activity). */
  resetIdleTimer: () => void;
}
export function useSessionTimer({
  isFluency,
  phase,
  currentQuestionIndex,
  isCheatSheetOpen,
  sessionQuestions,
  questionStartTimeRef
}: UseSessionTimerParams): SessionTimer {
  // ── Fluency elapsed timer ──────────────────────────────────
  const [elapsedMs, setElapsedMs] = useState(0);

  // ── Skip-eligibility timer ─────────────────────────────────
  const [canSkipTimePassed, setCanSkipTimePassed] = useState(false);
  const skipTimeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // ── Idle nudge ─────────────────────────────────────────────
  const [isIdle, setIsIdle] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const idleShownForCurrentQuestionRef = useRef(false);
  const lastIdleShownAtRef = useRef(0);
  const idleSoundPlayedRef = useRef(false);

  // Compute per-question idle timeout based on question prompt length.
  // We read from sessionQuestions directly (not `currentQuestion`) to keep
  // the same defensive semantics the inline version had.
  const idleTimeoutMs = useMemo(() => {
    const q = sessionQuestions[currentQuestionIndex];
    const textLen = (q?.question || '').length;
    const bonus = Math.floor(textLen / 20) * IDLE_PER_20_CHARS_MS;
    return Math.min(IDLE_MAX_MS, IDLE_BASE_MS + bonus);
  }, [sessionQuestions, currentQuestionIndex]);
  const dismissIdle = useCallback(() => {
    setIsIdle(false);
  }, []);
  const resetIdleTimer = useCallback(() => {
    setIsIdle(false);
    clearTimeout(idleTimerRef.current);
    // Only run idle timer during active question phase, and only if the
    // bubble hasn't already been shown for this question.
    if (phase !== 'question' || isCheatSheetOpen || idleShownForCurrentQuestionRef.current) {
      return;
    }
    idleTimerRef.current = setTimeout(() => {
      // Respect global cooldown across questions
      const now = Date.now();
      if (now - lastIdleShownAtRef.current < IDLE_GLOBAL_COOLDOWN_MS) {
        return;
      }
      idleShownForCurrentQuestionRef.current = true;
      lastIdleShownAtRef.current = now;
      setIsIdle(true);
      // Sound only on the first idle of the session
      if (!idleSoundPlayedRef.current) {
        playSoundIntent('mascot.speak');
        idleSoundPlayedRef.current = true;
      }
    }, idleTimeoutMs);
  }, [phase, isCheatSheetOpen, idleTimeoutMs]);

  // Reset on phase change or cheatsheet toggle
  useEffect(() => {
    resetIdleTimer();
    return () => clearTimeout(idleTimerRef.current);
  }, [resetIdleTimer]);

  // Reset per-question idle state whenever the question changes
  useEffect(() => {
    idleShownForCurrentQuestionRef.current = false;
    setIsIdle(false);
  }, [currentQuestionIndex]);

  // ── Fluency elapsed timer (4 Hz — F-PE-1 mitigation, PRESERVED) ─────
  useEffect(() => {
    if (!isFluency || phase !== 'question') {
      setElapsedMs(0);
      return;
    }
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - questionStartTimeRef.current);
    }, 250);
    return () => clearInterval(interval);
  }, [isFluency, phase, currentQuestionIndex, questionStartTimeRef]);

  // ── Per-question 20 s timer for skip eligibility ───────────
  useEffect(() => {
    setCanSkipTimePassed(false);
    clearTimeout(skipTimeTimerRef.current);
    if (phase !== 'question') return;
    skipTimeTimerRef.current = setTimeout(() => {
      setCanSkipTimePassed(true);
    }, SKIP_TIME_THRESHOLD_MS);
    return () => clearTimeout(skipTimeTimerRef.current);
  }, [currentQuestionIndex, phase]);

  // ── Cleanup all timers on unmount ──────────────────────────
  useEffect(() => {
    return () => {
      clearTimeout(idleTimerRef.current);
      clearTimeout(skipTimeTimerRef.current);
    };
  }, []);
  return {
    elapsedMs,
    canSkipTimePassed,
    isIdle,
    setIsIdle,
    dismissIdle,
    resetIdleTimer
  };
}